/**
 * Routine Surveillance plot data helpers
 *
 * Pure functions shared between the small-multiples plots view (RsDataPlots)
 * and the full-page single-plot view (RsDataPlotPage) so both parse rows,
 * compute dot sizes, and fetch a single country+metric series identically.
 *
 * Keeping these here (rather than duplicated per component) guarantees the two
 * views can never drift in how they interpret dates, values, or day spans.
 */

import {
  getAllRoutineSurveillanceData,
  RoutineSurveillanceRow,
  RoutineSurveillanceTopic,
} from "@/services/routineSurveillanceService"
import { ScatterPoint } from "@/components/rs-data/ScatterPlot"

/** Number of milliseconds in a day, used to convert a date span to whole days. */
const MS_PER_DAY: number = 1000 * 60 * 60 * 24

/**
 * URL/path token representing the "All Countries" selection. Lower-case so it
 * reads cleanly in the address bar (e.g. /data/rs/national/graph/all) and can
 * never collide with a real ISO3 code (which are upper-case).
 */
export const ALL_COUNTRIES_TOKEN = "all"

/**
 * The admin_level value of a national (country-aggregate) row. The national
 * plot data fetchers only ever request national data, so they constrain
 * admin_level to this value. A string because filter values serialise as
 * strings (the backend treats admin_level as a numeric column and renders it
 * unquoted). Subnational fetchers instead constrain to a specific non-zero
 * admin level chosen by the user.
 */
const NATIONAL_ADMIN_LEVEL: string = "0"

/**
 * URL/path token representing the "All Admin Units" selection in the
 * subnational view. Lower-case so it reads cleanly in the address bar
 * (e.g. /data/rs/subnational/plots/KEN/1/all) and can never collide with a
 * real map_admin_id (which are numeric/opaque ids, never this literal).
 */
export const ALL_ADMIN_UNITS_TOKEN = "all"

/**
 * Parse a row's date string into a Date, returning null when it is missing or
 * unparseable.
 *
 * Fails soft (returns null) rather than throwing: a single malformed date
 * should drop that one point, not abort the whole chart. Callers filter out
 * null results.
 *
 * @param value - The raw date string from the row (may be null).
 * @returns The parsed Date, or null when the value is missing/invalid.
 */
export function parseRowDate(value: string | null): Date | null {
  if (value === null || value.trim() === "") {
    return null
  }
  const parsed: Date = new Date(value)
  // An invalid date parses to NaN time; reject it rather than plotting at an
  // undefined x position.
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Parse a row's value string into a number, returning null when it is missing
 * or unparseable. Fails soft per-row, like parseRowDate.
 *
 * @param value - The raw value string from the row (may be null).
 * @returns The parsed number, or null when the value is missing/invalid.
 */
export function parseRowValue(value: string | null): number | null {
  if (value === null || value.trim() === "") {
    return null
  }
  const parsed: number = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Compute the number of days a data point covers, from its start and end dates.
 *
 * Returns null (rather than a guessed value) when either date is missing or
 * unparseable, or when the end precedes the start — so the caller sizes the dot
 * by the default radius instead of a misleading one. A start_date equal to
 * end_date yields a span of 0 (a single-day point).
 *
 * @param startDate - The already-parsed start date of the point.
 * @param endRaw - The raw end_date string from the row (may be null).
 * @returns The whole-day span (>= 0), or null when it cannot be determined.
 */
export function computeDaySpan(startDate: Date, endRaw: string | null): number | null {
  const endDate: Date | null = parseRowDate(endRaw)
  if (endDate === null) {
    return null
  }
  const spanMs: number = endDate.getTime() - startDate.getTime()
  // A negative span means end precedes start — treat as unknown rather than
  // sizing the dot by a nonsensical value.
  if (spanMs < 0) {
    return null
  }
  return Math.round(spanMs / MS_PER_DAY)
}

/**
 * Build a single scatter point from a row, or null when the row lacks a
 * plottable date or value.
 *
 * Shared so every view applies identical parsing/sizing rules regardless of how
 * the plots are grouped.
 *
 * @param row - The routine surveillance row to convert.
 * @returns The scatter point, or null when the row can't be placed on both axes.
 */
export function rowToScatterPoint(row: RoutineSurveillanceRow): ScatterPoint | null {
  const date: Date | null = parseRowDate(row.start_date)
  const value: number | null = parseRowValue(row.value)
  if (date === null || value === null) {
    return null
  }
  // Day span sizes the dot. Null when it can't be computed, in which case the
  // plot falls back to a default dot radius for this point.
  const daySpan: number | null = computeDaySpan(date, row.end_date)
  return {
    date,
    value,
    ...(daySpan !== null ? { sizeValue: daySpan } : {}),
  }
}

/**
 * "Category" columns whose uniformity is checked for the per-plot "Category
 * differences" note. These describe the population/segmentation a data point
 * belongs to; a single plot mixing more than one distinct value in any of them
 * means the plotted points are not strictly comparable, which the note flags.
 *
 * Deliberately EXCLUDES identity/measure/date columns (map_admin_id, iso3,
 * start_date, end_date, the species-count columns pf/pv/po/pm/pk, value, and the
 * various id columns) as well as metric_name (a plot is already scoped to one
 * metric or one country+metric, so it never varies within a plot).
 *
 * Each entry pairs the row key with the display label used in the note tooltip.
 */
export const CATEGORY_COLUMNS: {
  key: keyof RoutineSurveillanceRow
  label: string
}[] = [
  { key: "facility_type", label: "Facility Type" },
  { key: "sector", label: "Sector" },
  { key: "origin_name", label: "Origin" },
  { key: "case_detection_name", label: "Case Detection" },
  { key: "is_malignant", label: "Malignant" },
  { key: "is_mixed_infection", label: "Mixed Infection" },
  { key: "is_tertian", label: "Tertian" },
  { key: "pregnant_only", label: "Pregnant Only" },
  { key: "sex", label: "Sex" },
  { key: "urbanisation", label: "Urbanisation" },
  { key: "lower_age_years", label: "Lower Age" },
  { key: "upper_age_years", label: "Upper Age" },
  { key: "consolidated_species_code", label: "Species Code" },
]

/**
 * The lower/upper age-bound columns, which describe two ends of a single age
 * range. They are treated as a PAIR when labelling a category combination: if
 * either bound is shown (because it is non-default), the other is shown too —
 * even at its default value — so an age range never reads with only one bound.
 * See the label-building logic in computeCategoryCombinations.
 */
const AGE_BOUND_KEYS: readonly (keyof RoutineSurveillanceRow)[] = [
  "lower_age_years",
  "upper_age_years",
]

/**
 * The "typical" (expected/default) values for each category column, as the raw
 * string forms that arrive from the API (booleans as "true"/"false", numbers as
 * their string forms). When a plot's rows are uniform on a column but that
 * single shared value is NOT one of these, it is surfaced as an "atypical value"
 * (see computeAtypicalValues). A column may have several acceptable typical
 * values (e.g. legacy placeholders).
 */
const TYPICAL_VALUES: Record<string, string[]> = {
  facility_type: [
    "Assume all: data created before facility type dimension",
    "N/A",
    "All",
  ],
  sector: [
    "Assume public: data created before sector dimension",
    "N/A",
    "All",
    "Unspecified",
  ],
  origin_name: ["N/A", "Unspecified", "All"],
  case_detection_name: ["N/A", "Unspecified"],
  is_malignant: ["false"],
  is_mixed_infection: ["false"],
  is_tertian: ["false"],
  pregnant_only: ["false"],
  sex: ["All"],
  urbanisation: ["All"],
  lower_age_years: ["0"],
  upper_age_years: ["100"],
  consolidated_species_code: ["ANY_MALARIA"],
}

/**
 * Category columns whose typical-value comparison is NUMERIC rather than exact
 * string. The API returns these as decimals (e.g. "0.0", "100.0"), so comparing
 * the raw string to "0"/"100" would spuriously flag them; instead both sides
 * are parsed as numbers so "0", "0.0", "0.00" all match the typical value.
 */
const NUMERIC_CATEGORY_COLUMNS: ReadonlySet<string> = new Set([
  "lower_age_years",
  "upper_age_years",
])

/**
 * Test whether a raw column value matches one of its typical values.
 *
 * For numeric columns the comparison is by parsed number (so "0.0" == "0"); for
 * all others it is an exact string match. A numeric column whose value can't be
 * parsed falls back to string comparison rather than silently matching.
 *
 * @param key - The category column key.
 * @param raw - The raw (non-empty) value from the row.
 * @param typicalValues - The configured typical values for the column.
 * @returns True when the value is considered typical.
 */
function isTypicalValue(
  key: string,
  raw: string,
  typicalValues: string[]
): boolean {
  if (NUMERIC_CATEGORY_COLUMNS.has(key)) {
    const rawNum = Number(raw)
    if (!Number.isNaN(rawNum)) {
      return typicalValues.some((t) => {
        const tNum = Number(t)
        return !Number.isNaN(tNum) && tNum === rawNum
      })
    }
    // Unparseable numeric value: fall through to exact string comparison.
  }
  return typicalValues.includes(raw)
}

/** A single atypical category value: the column's label and its shared value. */
export interface AtypicalValue {
  /** The category column's display label. */
  label: string
  /** The shared value across the rows, which is not a typical value. */
  value: string
}

/**
 * Determine which category columns hold a single shared value that is NOT
 * typical, for a set of rows.
 *
 * Intended to be called only when the rows are uniform across the category
 * columns (i.e. computeCategoryDifferences returned nothing) — it reports, for
 * each column, the shared value when that value is not in TYPICAL_VALUES.
 *
 * For each column it reads the first row's value as the shared value (rows are
 * assumed uniform). null/empty values are skipped (absence is not "atypical").
 * Comparison is by raw string, matching how booleans ("true"/"false") and
 * numbers ("0"/"100") arrive from the API.
 *
 * @param rows - The uniform rows behind one plot (only those plotted/visible).
 * @returns The atypical {label, value} entries in CATEGORY_COLUMNS order. Empty
 *          when every column holds a typical value (or is null/empty).
 */
export function computeAtypicalValues(
  rows: RoutineSurveillanceRow[]
): AtypicalValue[] {
  const atypical: AtypicalValue[] = []
  if (rows.length === 0) {
    return atypical
  }

  for (const { key, label } of CATEGORY_COLUMNS) {
    // Rows are assumed uniform here, so the first row's value is the shared one.
    const raw: string | null | undefined = rows[0][key]
    // Skip absent values — absence is not surfaced as atypical.
    if (raw === null || raw === undefined || raw.trim() === "") {
      continue
    }
    const typicalForColumn: string[] | undefined = TYPICAL_VALUES[key]
    // A column with no configured typical values is not checked.
    if (typicalForColumn === undefined) {
      continue
    }
    if (!isTypicalValue(key, raw, typicalForColumn)) {
      atypical.push({ label, value: raw })
    }
  }

  return atypical
}

/**
 * Sentinel bucket that ALL of a column's typical values (and null/empty) are
 * collapsed into when counting distinct values for diversity. Using one shared
 * bucket means a column is only "diverse" if it carries more than one distinct
 * NON-typical value, or one non-typical value alongside a typical one — a mix of
 * typical values alone (e.g. "All" + "Unspecified") counts as a single value and
 * is therefore uniform. Chosen to never collide with a real column value.
 */
const TYPICAL_BUCKET = "\u0000__TYPICAL__"

/**
 * Determine which category columns vary across a set of rows.
 *
 * For each column in CATEGORY_COLUMNS, collects the distinct values seen across
 * the given rows and counts them — but ALL of the column's typical values (see
 * TYPICAL_VALUES), plus null/empty, collapse into a single shared bucket. So a
 * column with only typical values (in any mix) is uniform; it is "diverse" only
 * when it holds more than one distinct value once typicals are treated as one.
 *
 * Examples for `sector` (typical: All / Unspecified / …):
 *   - "All", "Unspecified"           → {typical}                → uniform
 *   - "All", "Unspecified", "Public" → {typical, "Public"}      → diverse
 *   - "Public", "Private"            → {"Public", "Private"}    → diverse
 *
 * Intended to be called with exactly the rows a single plot displays.
 *
 * @param rows - The rows behind one plot (only those actually plotted/visible).
 * @returns The display labels of the category columns that differ, in
 *          CATEGORY_COLUMNS order. Empty when every category column is uniform.
 */
export function computeCategoryDifferences(
  rows: RoutineSurveillanceRow[]
): string[] {
  const differingLabels: string[] = []

  for (const { key, label } of CATEGORY_COLUMNS) {
    const typicalForColumn: string[] | undefined = TYPICAL_VALUES[key]
    const distinct = new Set<string>()
    for (const row of rows) {
      // The value may be undefined (not null) when the row was fetched with a
      // column projection that omitted this column — guard for both.
      const raw: string | null | undefined = row[key]

      if (raw === null || raw === undefined || raw.trim() === "") {
        // null/undefined/empty collapse into the typical bucket — a missing
        // value is not a distinct "difference".
        distinct.add(TYPICAL_BUCKET)
      } else if (
        typicalForColumn !== undefined &&
        isTypicalValue(key, raw, typicalForColumn)
      ) {
        // Any typical value collapses into the shared bucket, so a mix of
        // different typical values counts as one.
        distinct.add(TYPICAL_BUCKET)
      } else {
        // A non-typical value counts as itself.
        distinct.add(raw)
      }

      // Short-circuit: two distinct buckets already means this column differs.
      if (distinct.size > 1) {
        break
      }
    }
    if (distinct.size > 1) {
      differingLabels.push(label)
    }
  }

  return differingLabels
}

/**
 * Normalise a single category column's raw value for grouping/labelling.
 *
 * Applies the SAME collapsing rule as computeCategoryDifferences: any typical
 * value (per TYPICAL_VALUES) and any null/empty value map to the shared
 * TYPICAL_BUCKET sentinel, so rows that differ only in WHICH default/typical
 * value they carry (e.g. sector "All" vs "Unspecified") are treated as the same
 * group. Non-typical values map to themselves.
 *
 * @param key - The category column key.
 * @param raw - The raw value from the row (may be null/undefined).
 * @returns The normalised value: the raw value, or TYPICAL_BUCKET when typical
 *          or absent.
 */
function normaliseCategoryValue(
  key: keyof RoutineSurveillanceRow,
  raw: string | null | undefined
): string {
  if (raw === null || raw === undefined || raw.trim() === "") {
    return TYPICAL_BUCKET
  }
  const typicalForColumn: string[] | undefined = TYPICAL_VALUES[key]
  if (
    typicalForColumn !== undefined &&
    isTypicalValue(key, raw, typicalForColumn)
  ) {
    return TYPICAL_BUCKET
  }
  return raw
}

/** One distinct category-combination group across a set of rows. */
export interface CategoryCombination {
  /**
   * Stable group id: a join of the normalised values of the DIFFERING category
   * columns (typical/default values collapsed into one bucket). Rows sharing
   * this id belong to the same group. Empty string when no column differs
   * (a single group covering every row).
   */
  id: string
  /**
   * Human-readable label built from ONLY the columns that differ across the
   * rows, e.g. "Sector: Public, Sex: Male". A differing column that holds the
   * typical/default value for this row reads as "<Label>: (default)". Reads
   * "All records" when no column differs (the single-group case).
   */
  label: string
}

/** The result of assigning each row to a category-combination group. */
export interface CategoryCombinationResult {
  /**
   * The distinct groups, in first-seen order across the rows. Callers map these
   * to colours/legend entries. Length 1 (the "All records" group) when no
   * category column differs.
   */
  combinations: CategoryCombination[]
  /**
   * The group id for each input row, aligned by index to the `rows` array
   * passed in. Use this to colour a row's plotted point by its group.
   */
  idByRowIndex: string[]
}

/**
 * Group rows by their combination of category values, considering only the
 * category columns that actually DIFFER across the given rows.
 *
 * Grouping and labelling both collapse typical/default values (and null/empty)
 * into a single bucket — see normaliseCategoryValue — so two rows that differ
 * only in which default value they carry share a group. Columns that do not
 * vary across the rows are excluded from both the id and the label, keeping
 * labels short (only the meaningful distinctions are shown).
 *
 * @param rows - The rows behind one plot (only those actually plotted/visible).
 * @returns The distinct combinations (first-seen order) and, aligned by index,
 *          the group id each row belongs to. For empty input, an empty result.
 */
export function computeCategoryCombinations(
  rows: RoutineSurveillanceRow[]
): CategoryCombinationResult {
  if (rows.length === 0) {
    return { combinations: [], idByRowIndex: [] }
  }

  // The columns (label + key) that vary across the rows, in CATEGORY_COLUMNS
  // order. Only these contribute to a group's id and label.
  const differingLabels: Set<string> = new Set(computeCategoryDifferences(rows))
  const differingColumns: { key: keyof RoutineSurveillanceRow; label: string }[] =
    CATEGORY_COLUMNS.filter((col) => differingLabels.has(col.label))

  // Build, per row, the normalised value of each differing column. The group id
  // is a join of those values with a separator that can't appear in the data.
  const ID_SEPARATOR: string = "\u0001"

  const idByRowIndex: string[] = []
  const combinationsById: Map<string, CategoryCombination> = new Map()

  rows.forEach((row) => {
    // Normalise each differing column's value for this row.
    const normalisedValues: string[] = differingColumns.map((col) =>
      normaliseCategoryValue(col.key, row[col.key])
    )
    const id: string = normalisedValues.join(ID_SEPARATOR)
    idByRowIndex.push(id)

    // Register the group the first time it is seen, building its label from the
    // differing columns only.
    if (!combinationsById.has(id)) {
      let label: string
      if (differingColumns.length === 0) {
        // No column differs → one group covering every row.
        label = "All records"
      } else {
        // Which differing columns to actually show for this row. Start with the
        // columns whose value is NON-default (a column holding the collapsed
        // typical/default value is otherwise dropped from the label rather than
        // shown as "(default)"), keeping the label to its meaningful
        // distinctions.
        const shownKeys: Set<keyof RoutineSurveillanceRow> = new Set()
        differingColumns.forEach((col, index) => {
          if (normalisedValues[index] !== TYPICAL_BUCKET) {
            shownKeys.add(col.key)
          }
        })

        // Age bounds are a pair: if EITHER lower/upper age is shown, force the
        // other in too (even at its default), so an age range never appears
        // with a single bound. Only applies when the partner is itself a
        // differing column — a bound that never varies isn't part of this
        // group's distinctions and stays out.
        const differingKeys: Set<keyof RoutineSurveillanceRow> = new Set(
          differingColumns.map((col) => col.key)
        )
        const anyAgeShown: boolean = AGE_BOUND_KEYS.some((key) => shownKeys.has(key))
        if (anyAgeShown) {
          for (const ageKey of AGE_BOUND_KEYS) {
            if (differingKeys.has(ageKey)) {
              shownKeys.add(ageKey)
            }
          }
        }

        // Build the label parts in CATEGORY_COLUMNS order. A forced age partner
        // that sits at its default shows its RAW row value (e.g. "0"/"100")
        // rather than the collapsed bucket, so the range reads correctly.
        const parts: string[] = differingColumns
          .filter((col) => shownKeys.has(col.key))
          .map((col): string => {
            const raw: string | null | undefined = row[col.key]
            const shown: string =
              raw === null || raw === undefined || raw.trim() === ""
                ? "N/A"
                : raw
            return `${col.label}: ${shown}`
          })

        // A group that is default across every differing column has no parts to
        // show; label it "Typical" so it stays distinct from the non-default
        // groups in the legend.
        label = parts.length > 0 ? parts.join(", ") : "Typical"
      }
      combinationsById.set(id, { id, label })
    }
  })

  // Preserve first-seen order for stable colour/legend assignment.
  const seen: Set<string> = new Set()
  const combinations: CategoryCombination[] = []
  for (const id of idByRowIndex) {
    if (!seen.has(id)) {
      seen.add(id)
      const combination: CategoryCombination | undefined = combinationsById.get(id)
      if (combination !== undefined) {
        combinations.push(combination)
      }
    }
  }

  return { combinations, idByRowIndex }
}

/** The result of fetching one country+metric's national scatter series. */
export interface CountryMetricPointsResult {
  /** The parsed points for the plot (date/value/day-span). */
  points: ScatterPoint[]
  /**
   * True when the backend capped the result and it is therefore incomplete.
   * Callers should surface this rather than plotting a partial set.
   */
  truncated: boolean
}

/** The result of fetching one country+metric's full national rows. */
export interface CountryMetricRowsResult {
  /**
   * The full, unprojected rows for the query — every column — so callers can
   * render a complete data table.
   */
  rows: RoutineSurveillanceRow[]
  /**
   * The parsed scatter points derived from the same rows. Kept alongside the
   * rows (rather than fetched separately) so the plot and the table are
   * guaranteed to show the same data.
   */
  points: ScatterPoint[]
  /**
   * True when the backend capped the result and it is therefore incomplete.
   * Callers should surface this rather than plotting/tabulating a partial set.
   */
  truncated: boolean
}

/**
 * Fetch the national (ADMIN0) scatter points for a single country + metric.
 *
 * This is the exact query the full-page view needs and a single cell of the
 * all-countries grid: national rows for one country (its iso3 at admin_level 0)
 * constrained to one metric, projected to only the plotting columns.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The country's ISO3 code. National rows are selected by this
 *        iso3 at admin_level 0.
 * @param metric - The metric_name to plot.
 * @param topic - The topic partition to query ("incidence" or "mortality") so
 *        the series matches the topic currently being viewed.
 * @returns The parsed points and whether the result was truncated.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchCountryMetricPoints(
  accessToken: string,
  iso3: string,
  metric: string,
  topic: RoutineSurveillanceTopic
): Promise<CountryMetricPointsResult> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      metric_name: [metric],
      // National row for this one country: its iso3 at admin_level 0 (the
      // national aggregate). admin_level is numeric server-side; the value is a
      // numeric string.
      iso3: [iso3],
      admin_level: [NATIONAL_ADMIN_LEVEL],
    },
    // Only the fields the scatter plot uses. end_date sizes each dot by the
    // number of days it covers.
    columns: ["start_date", "end_date", "value"],
  })

  const points: ScatterPoint[] = []
  for (const row of bulk.items) {
    const point: ScatterPoint | null = rowToScatterPoint(row)
    if (point !== null) {
      points.push(point)
    }
  }

  return { points, truncated: bulk.truncated }
}

/**
 * Fetch the FULL national (ADMIN0) rows for a single country + metric, along
 * with the scatter points derived from them.
 *
 * Unlike fetchCountryMetricPoints, this requests every column (no `columns`
 * projection) so a caller can render a complete data table of exactly what is
 * plotted. The plot points are derived from the same rows here, so the table
 * and plot can never disagree. Prefer fetchCountryMetricPoints when only the
 * plot is needed, since the projected query transfers far less data.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The country's ISO3 code. National rows are selected by this
 *        iso3 at admin_level 0.
 * @param metric - The metric_name to plot.
 * @param topic - The topic partition to query ("incidence" or "mortality") so
 *        the rows match the topic currently being viewed.
 * @returns The full rows, the derived scatter points, and the truncation flag.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchCountryMetricRows(
  accessToken: string,
  iso3: string,
  metric: string,
  topic: RoutineSurveillanceTopic
): Promise<CountryMetricRowsResult> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      metric_name: [metric],
      // National row for this one country: its iso3 at admin_level 0.
      iso3: [iso3],
      admin_level: [NATIONAL_ADMIN_LEVEL],
    },
    // No `columns` projection — return every column so the table is complete.
  })

  const points: ScatterPoint[] = []
  for (const row of bulk.items) {
    const point: ScatterPoint | null = rowToScatterPoint(row)
    if (point !== null) {
      points.push(point)
    }
  }

  return { rows: bulk.items, points, truncated: bulk.truncated }
}

// ---------------------------------------------------------------------------
// Subnational helpers
// ---------------------------------------------------------------------------

/**
 * A single selectable admin unit within a country + admin level: its stable
 * map_admin_id (used to identify/scope the unit's rows) paired with its
 * human-readable admin_unit_name (shown in the dropdown and plot titles).
 *
 * Admin unit names are not guaranteed unique across a country (two different
 * parent regions can contain a like-named unit), so map_admin_id — which is
 * unique per unit — is the identity, and the name is display only.
 */
export interface AdminUnitOption {
  /** Stable, unique id of the admin unit. Used to scope/group the unit's rows. */
  mapAdminId: string
  /** Human-readable admin unit name shown to the user. */
  name: string
}

/**
 * Fetch the distinct admin units present for a given country and admin level.
 *
 * The facets endpoint only returns distinct values for a single column across
 * the whole snapshot/topic and cannot be scoped by other columns, so it cannot
 * answer "which admin units exist in this country at this level". Instead this
 * does a projected bulk fetch constrained to the country (iso3) and admin level,
 * returning only map_admin_id + admin_unit_name, then reduces the rows to the
 * distinct units. This backs the Admin Unit dropdown, which must list only the
 * units that actually have data for the current country + level selection.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The selected country's ISO3 code.
 * @param adminLevel - The selected (non-zero) admin level, as its numeric string
 *        (e.g. "1", "2"). Rows are constrained to this exact level.
 * @param topic - The topic partition to query ("incidence" or "mortality") so
 *        the units match the topic currently being viewed.
 * @returns The distinct admin units (sorted by name) and whether the underlying
 *          bulk result was truncated (in which case the unit list may be
 *          incomplete and the caller should surface that).
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchAdminUnitOptions(
  accessToken: string,
  iso3: string,
  adminLevel: string,
  topic: RoutineSurveillanceTopic
): Promise<{ units: AdminUnitOption[]; truncated: boolean }> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      // The country and the specific (non-zero) admin level the user chose.
      iso3: [iso3],
      admin_level: [adminLevel],
    },
    // Only the two fields needed to build the dropdown options, to keep the
    // payload small — this can span many rows per unit.
    columns: ["map_admin_id", "admin_unit_name"],
  })

  // Reduce the rows to distinct units keyed by map_admin_id (the stable id). A
  // unit with no name falls back to showing its raw id rather than being
  // dropped, so a unit with data never silently disappears from the dropdown.
  const byId = new Map<string, AdminUnitOption>()
  for (const row of bulk.items) {
    const mapAdminId: string | null = row.map_admin_id
    // A row with no map_admin_id can't identify a unit; skip it.
    if (mapAdminId === null || mapAdminId === "") continue
    if (!byId.has(mapAdminId)) {
      byId.set(mapAdminId, {
        mapAdminId,
        name: row.admin_unit_name ?? mapAdminId,
      })
    }
  }

  const units: AdminUnitOption[] = Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return { units, truncated: bulk.truncated }
}

/**
 * A single subnational plot series ready to render: a stable key, a display
 * title, its parsed scatter points, the source rows behind them, and the
 * identity (admin unit id + metric) used to build the full-page expand link.
 */
export interface SubnationalPlotSeries {
  /** Stable key for React and plot identity (map_admin_id or metric name). */
  key: string
  /** Title shown above the plot (admin unit name or metric name). */
  title: string
  /** The points (date on x, value on y, day-span on size) for this plot. */
  points: ScatterPoint[]
  /** The source rows behind this plot's points (for the "Category" note). */
  rows: RoutineSurveillanceRow[]
  /** map_admin_id this plot's data belongs to (for the expand link). */
  mapAdminId: string
  /** admin_unit_name this plot's data belongs to (for the expand link title). */
  adminUnitName: string
  /** metric_name this plot's data belongs to (for the expand link). */
  metric: string
}

/**
 * Fetch every subnational row for one country + admin level + metric, and build
 * one scatter series PER ADMIN UNIT (the "All Admin Units" mode).
 *
 * Analogous to the national all-countries mode, but grouped by admin unit
 * (map_admin_id) within a single country and admin level rather than by country.
 * Rows are grouped by their own map_admin_id so each unit gets its own plot.
 * Rows whose date/value can't be parsed are dropped per-row so one bad row never
 * voids a unit.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The selected country's ISO3 code.
 * @param adminLevel - The selected non-zero admin level (numeric string).
 * @param metric - The metric_name to plot across the country's admin units.
 * @param topic - The topic partition to query.
 * @returns One series per admin unit (sorted by unit name) and the truncation
 *          flag from the underlying bulk fetch.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchAdminUnitsForMetric(
  accessToken: string,
  iso3: string,
  adminLevel: string,
  metric: string,
  topic: RoutineSurveillanceTopic
): Promise<{ series: SubnationalPlotSeries[]; truncated: boolean }> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      iso3: [iso3],
      admin_level: [adminLevel],
      metric_name: [metric],
    },
    // map_admin_id + admin_unit_name group rows into per-unit plots; the
    // plotting fields plus the category columns back the points and the note.
    columns: [
      "map_admin_id",
      "admin_unit_name",
      "start_date",
      "end_date",
      "value",
      ...CATEGORY_COLUMNS.map((c) => c.key),
    ],
  })

  // Group parsed points (and their source rows) by the row's map_admin_id.
  const pointsById = new Map<string, ScatterPoint[]>()
  const rowsById = new Map<string, RoutineSurveillanceRow[]>()
  const nameById = new Map<string, string>()
  for (const row of bulk.items) {
    const mapAdminId: string | null = row.map_admin_id
    // A row with no unit id can't be assigned to a unit plot; skip it.
    if (mapAdminId === null || mapAdminId === "") continue

    // Remember a display name for the unit (first non-empty wins).
    if (!nameById.has(mapAdminId)) {
      nameById.set(mapAdminId, row.admin_unit_name ?? mapAdminId)
    }

    const point: ScatterPoint | null = rowToScatterPoint(row)
    if (point === null) continue

    const existing: ScatterPoint[] | undefined = pointsById.get(mapAdminId)
    if (existing === undefined) {
      pointsById.set(mapAdminId, [point])
      rowsById.set(mapAdminId, [row])
    } else {
      existing.push(point)
      rowsById.get(mapAdminId)?.push(row)
    }
  }

  const series: SubnationalPlotSeries[] = Array.from(pointsById.entries())
    .map(([mapAdminId, points]): SubnationalPlotSeries => {
      const adminUnitName: string = nameById.get(mapAdminId) ?? mapAdminId
      return {
        key: mapAdminId,
        title: adminUnitName,
        points,
        rows: rowsById.get(mapAdminId) ?? [],
        mapAdminId,
        adminUnitName,
        metric,
      }
    })
    .sort((a, b) => a.title.localeCompare(b.title))

  return { series, truncated: bulk.truncated }
}

/**
 * Fetch every subnational row for a single admin unit across all metrics, and
 * build one scatter series PER METRIC (the "specific admin unit" mode).
 *
 * Analogous to the national single-country mode, but scoped to one admin unit
 * (identified by its map_admin_id) rather than one country. Rows are grouped by
 * metric_name so each metric the unit has data for gets its own plot.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The selected country's ISO3 code (kept as an extra guard so the
 *        query is scoped to the country as well as the unit).
 * @param adminLevel - The selected non-zero admin level (numeric string).
 * @param mapAdminId - The specific admin unit's stable id to plot.
 * @param adminUnitName - The unit's display name, carried through onto each
 *        series so the expand link title is correct without a re-lookup.
 * @param topic - The topic partition to query.
 * @returns One series per metric (sorted by metric name) and the truncation
 *          flag from the underlying bulk fetch.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchMetricsForAdminUnit(
  accessToken: string,
  iso3: string,
  adminLevel: string,
  mapAdminId: string,
  adminUnitName: string,
  topic: RoutineSurveillanceTopic
): Promise<{ series: SubnationalPlotSeries[]; truncated: boolean }> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      iso3: [iso3],
      admin_level: [adminLevel],
      // The specific unit. map_admin_id is unique per unit, so this pins the
      // query to exactly one admin unit's rows.
      map_admin_id: [mapAdminId],
    },
    // metric_name groups rows into per-metric plots; the plotting fields plus
    // the category columns back the points and the note.
    columns: [
      "metric_name",
      "start_date",
      "end_date",
      "value",
      ...CATEGORY_COLUMNS.map((c) => c.key),
    ],
  })

  // Group parsed points (and their source rows) by metric_name.
  const pointsByMetric = new Map<string, ScatterPoint[]>()
  const rowsByMetric = new Map<string, RoutineSurveillanceRow[]>()
  for (const row of bulk.items) {
    const metric: string | null = row.metric_name
    // A row with no metric can't be assigned to a plot; skip it.
    if (metric === null || metric === "") continue

    const point: ScatterPoint | null = rowToScatterPoint(row)
    if (point === null) continue

    const existing: ScatterPoint[] | undefined = pointsByMetric.get(metric)
    if (existing === undefined) {
      pointsByMetric.set(metric, [point])
      rowsByMetric.set(metric, [row])
    } else {
      existing.push(point)
      rowsByMetric.get(metric)?.push(row)
    }
  }

  const series: SubnationalPlotSeries[] = Array.from(pointsByMetric.entries())
    .map(([metric, points]): SubnationalPlotSeries => ({
      key: metric,
      title: metric,
      points,
      rows: rowsByMetric.get(metric) ?? [],
      mapAdminId,
      adminUnitName,
      metric,
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  return { series, truncated: bulk.truncated }
}

/**
 * Fetch the FULL rows for a single admin unit + metric, along with the scatter
 * points derived from them — the subnational equivalent of
 * fetchCountryMetricRows, used by the full-page expand plot.
 *
 * Requests every column (no projection) so the full-page view can render a
 * complete data table, and derives the plot points from the same rows so the
 * plot and table can never disagree. The unit is identified by its stable
 * map_admin_id; iso3 and admin_level are included as extra guards so the query
 * is unambiguously scoped.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @param iso3 - The country's ISO3 code.
 * @param adminLevel - The non-zero admin level (numeric string).
 * @param mapAdminId - The specific admin unit's stable id.
 * @param metric - The metric_name to plot.
 * @param topic - The topic partition to query.
 * @returns The full rows, the derived scatter points, and the truncation flag.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function fetchAdminUnitMetricRows(
  accessToken: string,
  iso3: string,
  adminLevel: string,
  mapAdminId: string,
  metric: string,
  topic: RoutineSurveillanceTopic
): Promise<CountryMetricRowsResult> {
  const bulk = await getAllRoutineSurveillanceData(accessToken, {
    topic,
    filters: {
      iso3: [iso3],
      admin_level: [adminLevel],
      map_admin_id: [mapAdminId],
      metric_name: [metric],
    },
    // No `columns` projection — return every column so the table is complete.
  })

  const points: ScatterPoint[] = []
  for (const row of bulk.items) {
    const point: ScatterPoint | null = rowToScatterPoint(row)
    if (point !== null) {
      points.push(point)
    }
  }

  return { rows: bulk.items, points, truncated: bulk.truncated }
}
